import { Button, TextField } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls'
import { getTarifas, updateTarifas } from '../../../../api/contract'
import { alertError, alertSuccess } from '../../../../utils/logger'

/**
 * Envío y asistencia: las dos tarifas que se cambian sin desplegar.
 *
 * Dependen de un transportista y del precio de la gasolina, así que se van a
 * revisar varias veces al año. Pedir un despliegue para subir un envío tres
 * euros acaba en que no se sube y el precio publicado se queda viejo.
 *
 * LO DEMÁS NO ESTÁ AQUÍ A PROPÓSITO. Los packs, el alta y el pack de regata
 * son el modelo de negocio: están en `utils/packs.js` del backend, se cambian
 * en un commit que se lee, y así queda escrito quién los cambió y por qué.
 *
 * Y lo que se cambie aquí vale para las ventas de MAÑANA. Lo que se cobró en
 * un contrato ya firmado quedó congelado en su contrato, igual que el número
 * de vela de un participante: subir el envío no reescribe una factura vieja.
 */
export default function TarifasPage() {
	const [tarifas, setTarifas] = useState(null)
	const [guardando, setGuardando] = useState(false)
	const history = useHistory()

	useEffect(() => {
		getTarifas()
			.then((res) => setTarifas(res.data))
			.catch((error) => alertError({ error, customMessage: 'No se pudieron leer las tarifas.' }))
	}, [])

	/** Se escribe en euros y se guarda en céntimos: nadie teclea «1500» por 15 €. */
	const enEuros = (centimos) => (centimos == null ? '' : String(centimos / 100))
	const aCentimos = (euros) => Math.round(Number(String(euros).replace(',', '.')) * 100)

	const cambiar = (grupo, campo) => (e) => {
		const v = e.target.value
		setTarifas((t) => ({ ...t, [grupo]: { ...t[grupo], [campo]: v === '' ? '' : aCentimos(v) } }))
	}

	function guardar() {
		setGuardando(true)
		updateTarifas(tarifas)
			.then((res) => {
				setTarifas(res.data)
				alertSuccess({
					title: 'Tarifas guardadas',
					customMessage: 'Valen para las altas nuevas. Los contratos ya firmados no se tocan.',
				})
			})
			.catch((error) => alertError({ error, customMessage: 'No se pudieron guardar las tarifas.' }))
			.finally(() => setGuardando(false))
	}

	if (!tarifas)
		return (
			<Card>
				<CardHeader title="Tarifas de envío y asistencia" />
				<CardBody>Cargando…</CardBody>
			</Card>
		)

	const campo = (grupo, nombre, etiqueta, ayuda) => (
		<TextField
			key={`${grupo}.${nombre}`}
			label={etiqueta}
			helperText={ayuda}
			type="number"
			value={enEuros(tarifas[grupo]?.[nombre])}
			onChange={cambiar(grupo, nombre)}
			margin="normal"
			fullWidth
			inputProps={{ min: 0, step: '0.05' }}
		/>
	)

	// Con el km a 0,45 €, Cubelles (50 km) sale a 545 € y Palamós (120) a 608.
	const ejemplo = (km) =>
		new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(
			((tarifas.asistencia?.baseCentimos ?? 0) + km * 2 * (tarifas.asistencia?.centimosPorKm ?? 0)) / 100
		)

	return (
		<Card>
			<CardHeader title="Tarifas de envío y asistencia">
				<CardHeaderToolbar>
					<button type="button" className="btn btn-light" onClick={() => history.push('/contracts')}>
						Volver
					</button>
				</CardHeaderToolbar>
			</CardHeader>
			<CardBody>
				<p style={{ color: '#7b8794', maxWidth: '60ch' }}>
					En euros y sin IVA. Salen publicadas en sailradar.com y se aplican al contratar. Los packs, el
					alta y el pack de regata no se tocan desde aquí: son el modelo y viven en el código.
				</p>

				<h5 style={{ marginTop: 24 }}>Envío, por caja</h5>
				<p style={{ color: '#7b8794' }}>En una caja caben unos 25 aparatos. 40 aparatos son dos cajas.</p>
				{campo('envio', 'peninsula', 'Península', 'Lo habitual')}
				{campo('envio', 'baleares', 'Baleares')}
				{campo('envio', 'canarias', 'Canarias')}
				{campo('envio', 'ue', 'Resto de la UE')}

				<h5 style={{ marginTop: 32 }}>Asistencia</h5>
				<p style={{ color: '#7b8794' }}>
					Vamos nosotros y lo montamos. El desplazamiento va dentro: los kilómetros se cuentan de ida y
					vuelta, así que no hay ninguna frontera que explicar por teléfono.
				</p>
				{campo('asistencia', 'baseCentimos', 'La jornada', 'Sin contar el desplazamiento')}
				{campo('asistencia', 'centimosPorKm', 'Por kilómetro', 'Se multiplica por dos: ida y vuelta')}
				<p style={{ color: '#7b8794' }}>
					Con estos números: Cubelles (50 km) <strong>{ejemplo(50)}</strong> · Palamós (120 km){' '}
					<strong>{ejemplo(120)}</strong>
				</p>

				<Button
					variant="contained"
					color="primary"
					onClick={guardar}
					disabled={guardando}
					style={{ marginTop: 24 }}
				>
					Guardar
				</Button>
			</CardBody>
		</Card>
	)
}
